import {LOCAL_API_ERROR_HANDLER} from './local-api-error-handler';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {Observable, catchError, from, mergeMap, throwError, TimeoutError} from 'rxjs';
import {ServerErrorDialogService} from './server-error-dialog.service';
import {buildApiUrl} from '../api/api-url';
import {apiErrorDetails} from './api-error-details';
import {isLocallyHandledApiError, LOCALLY_HANDLED_API_ERROR_CODES} from './local-api-error';
import {ClientLoggerService} from '../logging/client-logger.service';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  constructor(private readonly injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.error instanceof Blob && request.context.get(LOCALLY_HANDLED_API_ERROR_CODES).length) {
          return from(this.decodeBlobError(error)).pipe(mergeMap(decoded => this.handleError(request, decoded)));
        }
        return this.handleError(request, error);
      }),
    );
  }

  private handleError(request: HttpRequest<unknown>, error: unknown): Observable<never> {
    const apiUrl = buildApiUrl('/');
    if (request.url.startsWith(apiUrl) && error instanceof TimeoutError) {
      this.injector.get(ServerErrorDialogService).openSystem(error, '');
    } else if (request.url.startsWith(apiUrl) && error instanceof HttpErrorResponse
      && error.status !== 304 && error.status !== 401
      && !this.screenHandledError(request, error)
      && !isLocallyHandledApiError(error, request.context.get(LOCALLY_HANDLED_API_ERROR_CODES))) {
      this.logCriticalError(request, error);
      this.injector.get(ServerErrorDialogService).openSystem(error, apiErrorDetails(request, error));
    }
    return throwError(() => error);
  }

  private logCriticalError(request: HttpRequest<unknown>, error: HttpErrorResponse): void {
    // Offline/connection recovery is expected; only unhandled API responses are critical here.
    if (error.status === 0) return;
    try {
      this.injector.get(ClientLoggerService).critical('Unhandled API request failure', {
        httpStatus: error.status, httpMethod: request.method,
      });
    } catch { /* Preserve the original failure even if logging is unavailable. */ }
  }

  private screenHandledError(request: HttpRequest<unknown>, error: unknown): boolean {
    try {
      return request.context.get(LOCAL_API_ERROR_HANDLER)?.(error) === true;
    } catch {
      return false;
    }
  }

  private async decodeBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    const blob = error.error as Blob;
    if (blob.size > 65536 || !/application\/(?:json|problem\+json)/i.test(blob.type)) return error;
    try {
      return new HttpErrorResponse({headers: error.headers, status: error.status, statusText: error.statusText,
        url: error.url ?? undefined, error: JSON.parse(await blob.text())});
    } catch { return error; }
  }
}
